// frontend/src/billing/PricingPlans.js
// ═══════════════════════════════════════════════════════════════════════════
// VERSIÓN CORREGIDA v2 - Soporta TRES flujos de acceso:
//   1. Nuevo admin registrándose (viene con state.condominiumId)
//   2. Admin existente logueado (viene del dashboard sin state)
//   3. Visitante público viendo precios (sin login, solo informativo)
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPlansArray } from '../config/planConfig';
import LogoutRequiredModal from '../components/LogoutRequiredModal';
import './PricingPlans.css';

export default function PricingPlans() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showTrialOption, setShowTrialOption] = useState(false);
  const [selectedTrialPlan, setSelectedTrialPlan] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ DATOS DEL USUARIO - Identificar el tipo de acceso
  // ═══════════════════════════════════════════════════════════════════════════
  const [accessType, setAccessType] = useState('loading'); // 'registration', 'logged_in', 'public'
  const [userData, setUserData] = useState({
    userId: null,
    condominiumId: null,
    userName: null,
    userEmail: null,
    condominiumName: null
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ VALIDACIÓN DE ACCESO - Soporta los 3 flujos
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    console.log('🔐 Validando acceso a PricingPlans...');
    console.log('📦 State de navegación:', state);

    // ─────────────────────────────────────────────────────────────────────────
    // CASO 1: Usuario viene del FLUJO DE REGISTRO (tiene state.condominiumId)
    // ─────────────────────────────────────────────────────────────────────────
    if (state?.condominiumId) {
      console.log('✅ FLUJO: Nuevo registro - CondominiumId:', state.condominiumId);
      setUserData({
        userId: state.userId,
        condominiumId: state.condominiumId,
        userName: state.userName,
        userEmail: state.userEmail,
        condominiumName: state.condominiumName
      });
      setAccessType('registration');
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // CASO 2: Usuario ya LOGUEADO accede desde dashboard/navbar
    // ─────────────────────────────────────────────────────────────────────────
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const user = JSON.parse(storedUser);
        console.log('✅ FLUJO: Admin existente logueado -', user.name);
        
        if (user.condominium_id) {
          setUserData({
            userId: user.id,
            condominiumId: user.condominium_id,
            userName: user.name,
            userEmail: user.email,
            condominiumName: user.condominium_name || 'Mi Condominio'
          });
          setAccessType('logged_in');
          return;
        }
      } catch (error) {
        console.error('❌ Error parseando datos del usuario:', error);
      }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // CASO 3: Visitante PÚBLICO viendo precios (sin login)
    // Permitir ver la página pero sin poder seleccionar plan
    // ─────────────────────────────────────────────────────────────────────────
    console.log('ℹ️ FLUJO: Visitante público - Solo visualización');
    setAccessType('public');
    
  }, [state]);

  // Planes desde planConfig (fuente única de verdad)
  const plans = getPlansArray();

  const handleTrialSelection = (plan) => {
    // Si es visitante público, redirigir a registro
    if (accessType === 'public') {
      navigate('/register', {
        state: {
          message: 'Registra tu condominio para comenzar tu prueba gratis',
          selectedPlan: plan.id
        }
      });
      return;
    }

    // Si es admin logueado, mostrar modal de cerrar sesión
    if (accessType === 'logged_in') {
      setShowLogoutModal(true);
      return;
    }

    setSelectedTrialPlan(plan.id);
    setShowTrialOption(true);

    setTimeout(() => {
      document.getElementById('trial-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }, 100);
  };

  const selectPlan = (plan, isTrial = false) => {
    // ─────────────────────────────────────────────────────────────────────────
    // Si es visitante público, redirigir a registro
    // ─────────────────────────────────────────────────────────────────────────
    if (accessType === 'public') {
      navigate('/register', {
        state: {
          message: 'Registra tu condominio para continuar con la compra',
          selectedPlan: plan.id,
          isTrial
        }
      });
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Si es admin logueado, mostrar modal de cerrar sesión
    // ─────────────────────────────────────────────────────────────────────────
    if (accessType === 'logged_in') {
      setShowLogoutModal(true);
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Usuario en registro - proceder al checkout
    // ─────────────────────────────────────────────────────────────────────────
    if (!userData.condominiumId) {
      alert('Error: No se encontraron datos del condominio');
      navigate('/register');
      return;
    }
    
    setSelectedPlan(plan.id);
    
    // Navegar a la página de pago con los datos del plan
    setTimeout(() => {
      navigate('/billing/checkout', { 
        state: { 
          plan: {
            ...plan,
            isTrial,
            trialDays: isTrial ? plan.trialDays : 0,
            finalPrice: appliedDiscount 
              ? (plan.price * (1 - appliedDiscount.percentage / 100)).toFixed(2)
              : plan.price,
            discount: appliedDiscount
          },
          userId: userData.userId,
          condominiumId: userData.condominiumId,
          userName: userData.userName,
          userEmail: userData.userEmail,
          condominiumName: userData.condominiumName,
          accessType: accessType // Para saber de dónde viene
        } 
      });
    }, 500);
  };

  const applyDiscountCode = () => {
    const validCodes = {
      'BIENVENIDO2025': { percentage: 15, label: '15% OFF' },
      'REFERIDO10': { percentage: 10, label: '10% OFF' },
      'PRIMERANO20': { percentage: 20, label: '20% OFF' }
    };

    if (validCodes[discountCode.toUpperCase()]) {
      setAppliedDiscount({
        code: discountCode.toUpperCase(),
        ...validCodes[discountCode.toUpperCase()]
      });
    } else {
      alert('Código de descuento inválido');
    }
  };

  const calculateDiscountedPrice = (price) => {
    if (!appliedDiscount) return price;
    return (price * (1 - appliedDiscount.percentage / 100)).toFixed(2);
  };

  const getTrialPlan = () => {
    return plans.find(p => p.id === selectedTrialPlan);
  };

  // ✅ Handler para confirmar logout desde el modal
  const handleConfirmLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('condominiumId');
    setShowLogoutModal(false);
    navigate('/planes');
  };

  // ✅ Función para volver - diferente según el flujo
  const handleGoBack = () => {
    switch (accessType) {
      case 'registration':
        navigate('/login');
        break;
      case 'logged_in':
        navigate(-1); // Volver a la página anterior (dashboard)
        break;
      case 'public':
      default:
        navigate('/');
        break;
    }
  };

  // ✅ Mostrar loading mientras determina el tipo de acceso
  if (accessType === 'loading') {
    return (
      <div className="pricing-page">
        <div className="pricing-container">
          <div className="pricing-header">
            <h1 className="pricing-title">Cargando planes...</h1>
            <p className="pricing-subtitle">Por favor espera un momento</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Determinar el subtítulo según el tipo de acceso
  const getSubtitle = () => {
    switch (accessType) {
      case 'registration':
        return `Configura el plan para: ${userData.condominiumName || 'tu nuevo condominio'}`;
      case 'logged_in':
        return `Gestiona el plan de: ${userData.condominiumName}`;
      case 'public':
      default:
        return 'Sin costos ocultos. Cancela cuando quieras. Comienza hoy mismo.';
    }
  };

  // ✅ Determinar el texto del botón según el tipo de acceso
  const getButtonText = (plan) => {
    if (selectedPlan === plan.id) return 'Procesando...';
    if (accessType === 'public') return 'Comenzar ahora';
    return 'Seleccionar plan';
  };

  return (
    <div className="pricing-page">
      <div className="pricing-container">
        {/* Header */}
        <div className="pricing-header">
          <h1 className="pricing-title">Elige el plan perfecto para tu condominio</h1>
          <p className="pricing-subtitle">{getSubtitle()}</p>
          
          {/* Indicador de tipo de acceso (solo para debug, puedes removerlo) */}
          {/* <small style={{color: 'rgba(255,255,255,0.5)'}}>Modo: {accessType}</small> */}
          
          {/* Mensaje para visitantes públicos */}
          {accessType === 'public' && (
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '10px 20px',
              borderRadius: '8px',
              marginTop: '15px',
              fontSize: '0.95rem'
            }}>
              💡 <strong>¿Ya tienes cuenta?</strong>{' '}
              <button 
                onClick={() => navigate('/login')}
                style={{
                  background: 'white',
                  color: '#667eea',
                  border: 'none',
                  padding: '5px 15px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginLeft: '10px'
                }}
              >
                Iniciar Sesión
              </button>
            </div>
          )}
          
          {/* Discount Code Input */}
          <div className="discount-section">
            <div className="discount-input-wrapper">
              <input
                type="text"
                className="discount-input"
                placeholder="¿Tienes un código de descuento?"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && applyDiscountCode()}
              />
              <button className="apply-discount-btn" onClick={applyDiscountCode}>
                Aplicar
              </button>
            </div>
            {appliedDiscount && (
              <div className="discount-applied">
                ✅ Código "{appliedDiscount.code}" aplicado - {appliedDiscount.label}
                <button 
                  className="remove-discount" 
                  onClick={() => setAppliedDiscount(null)}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Plans Grid */}
        <div className="plans-grid">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''} ${plan.badge ? 'featured' : ''}`}
              style={{'--plan-color': plan.color}}
            >
              {plan.badge && <div className="plan-badge">{plan.badge}</div>}
              
              <div className="plan-header">
                <h3 className="plan-name">{plan.name}</h3>
                <div className="plan-duration">{plan.duration}</div>
              </div>

              <div className="plan-pricing">
                {plan.originalPrice && (
                  <div className="original-price">${plan.originalPrice}</div>
                )}
                <div className="plan-price">
                  <span className="currency">$</span>
                  <span className="amount">
                    {appliedDiscount ? calculateDiscountedPrice(plan.price) : plan.price}
                  </span>
                </div>
                {appliedDiscount && (
                  <div className="discounted-original">${plan.price}</div>
                )}
                <div className="price-detail">
                  ${plan.pricePerMonth}/mes
                </div>
                {(plan.savings || appliedDiscount) && (
                  <div className="savings-badge">
                    {appliedDiscount ? appliedDiscount.label : plan.savings}
                  </div>
                )}
              </div>

              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index} className="feature-item">
                    <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="plan-actions">
                {plan.hasTrial && (
                  <button 
                    className="trial-btn"
                    onClick={() => handleTrialSelection(plan)}
                  >
                    🎁 Prueba gratis {plan.trialDays} días
                  </button>
                )}
                <button 
                  className={`plan-button ${selectedPlan === plan.id ? 'processing' : ''}`}
                  onClick={() => selectPlan(plan, false)}
                  disabled={selectedPlan === plan.id}
                >
                  {getButtonText(plan)}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Trial Option Expanded Section */}
        {showTrialOption && getTrialPlan() && (
          <div className="trial-section" id="trial-section">
            <div className="trial-card">
              <div className="trial-header">
                <div className="trial-icon">🎉</div>
                <h2>¡Prueba {getTrialPlan().name} GRATIS por {getTrialPlan().trialDays} días!</h2>
                <p>Sin tarjeta de crédito. Sin compromiso. Cancela cuando quieras.</p>
              </div>

              <div className="trial-features">
                <div className="trial-feature">
                  <div className="trial-feature-icon">✅</div>
                  <div className="trial-feature-text">
                    <h4>Acceso completo</h4>
                    <p>Todas las funciones del plan {getTrialPlan().name}</p>
                  </div>
                </div>
                <div className="trial-feature">
                  <div className="trial-feature-icon">🔔</div>
                  <div className="trial-feature-text">
                    <h4>Recordatorio anticipado</h4>
                    <p>Te avisamos 3 días antes de que termine tu prueba</p>
                  </div>
                </div>
                <div className="trial-feature">
                  <div className="trial-feature-icon">💳</div>
                  <div className="trial-feature-text">
                    <h4>Sin cargos automáticos</h4>
                    <p>Tú decides si continuar después de la prueba</p>
                  </div>
                </div>
              </div>

              <div className="trial-timeline">
                <div className="timeline-step">
                  <div className="timeline-dot">1</div>
                  <div className="timeline-content">
                    <strong>Hoy</strong>
                    <p>Comienza tu prueba gratis</p>
                  </div>
                </div>
                <div className="timeline-line"></div>
                <div className="timeline-step">
                  <div className="timeline-dot">2</div>
                  <div className="timeline-content">
                    <strong>Día {getTrialPlan().trialDays - 3}</strong>
                    <p>Te enviamos un recordatorio</p>
                  </div>
                </div>
                <div className="timeline-line"></div>
                <div className="timeline-step">
                  <div className="timeline-dot">3</div>
                  <div className="timeline-content">
                    <strong>Día {getTrialPlan().trialDays}</strong>
                    <p>Decide si continuar o cancelar</p>
                  </div>
                </div>
              </div>

              <button 
                className="trial-cta-button"
                onClick={() => selectPlan(getTrialPlan(), true)}
              >
                ✨ Comenzar prueba gratis de {getTrialPlan().trialDays} días
              </button>

              <button 
                className="trial-close-button"
                onClick={() => setShowTrialOption(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        {/* Comparison Toggle */}
        <div className="comparison-toggle">
          <button 
            className="toggle-comparison-btn"
            onClick={() => setShowComparison(!showComparison)}
          >
            {showComparison ? '📋 Ocultar' : '📊 Comparar'} planes
          </button>
        </div>

        {/* Comparison Table */}
        {showComparison && (
          <div className="comparison-table">
            <table>
              <thead>
                <tr>
                  <th>Característica</th>
                  {plans.map(plan => (
                    <th key={plan.id} style={{color: plan.color}}>{plan.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Unidades permitidas</td>
                  <td>Hasta 50</td>
                  <td>Hasta 200</td>
                  <td>Ilimitadas</td>
                </tr>
                <tr>
                  <td>Reportes</td>
                  <td>Básicos</td>
                  <td>Avanzados</td>
                  <td>Avanzados + Personalizados</td>
                </tr>
                <tr>
                  <td>Soporte</td>
                  <td>Email</td>
                  <td>Prioritario</td>
                  <td>24/7 Dedicado</td>
                </tr>
                <tr>
                  <td>Backup</td>
                  <td>Manual</td>
                  <td>Semanal</td>
                  <td>Diario</td>
                </tr>
                <tr>
                  <td>API</td>
                  <td>-</td>
                  <td>Básica</td>
                  <td>Completa</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="pricing-footer">
          <div className="trust-indicators">
            <div className="trust-item">
              <span className="trust-icon">🔒</span>
              <span>Pago seguro SSL</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">💳</span>
              <span>PayPal verificado</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">📞</span>
              <span>Soporte incluido</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">✅</span>
              <span>30 días garantía</span>
            </div>
          </div>
          
          <button 
            className="back-link"
            onClick={handleGoBack}
          >
            ← Volver
          </button>
        </div>
      </div>

      {/* Modal de cierre de sesión requerido */}
      <LogoutRequiredModal
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirmLogout={handleConfirmLogout}
      />
    </div>
  );
}
